import { useParams } from "react-router-dom";
import ApiStatus from "../apiStatus";
import { useDeleteUnit, useFetchUnit, useUpdateUnit } from "../hooks/UnitHooks";
import ValidationSummary from "../ValidationSummary";
import UnitForm from "./UnitForm";
import TerminalList from "../terminal/TerminalList";
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from "react";
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from "../AuthContext";
import { UnitDTO } from "../types/unitDTO";

const UnitEdit = () => {
  const { t } = useTranslation();
  const { roles } = useContext(AuthContext);
  const { organisationId, unitId } = useParams<{ organisationId: string, unitId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  if (!unitId) throw Error("Need a unit id");
  const { data, status, isSuccess } = useFetchUnit(organisationId, unitId);
  const updateUnitMutation = useUpdateUnit(organisationId);
  const deleteUnitMutation = useDeleteUnit(organisationId);
  const [unitToDelete, setUnitToDelete] = useState<UnitDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const canDelete = roles.includes("SysAdmin") || roles.includes("OptiviAdmin");

  const handleUnitDelete = (unit: UnitDTO) => {
    setUnitToDelete(unit);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    if (!unitToDelete) return;

    deleteUnitMutation.mutate(unitToDelete, {
      onSuccess: () => {
        setShowDeleteModal(false);
        toast.success(t('unit.deleteSuccess'));
        history.back();
      },
      onError: () => {
        setShowDeleteModal(false);
        toast.error(t('unit.deleteError'));
      }
    });
  };

  if (!isSuccess) return <ApiStatus status={status} />;

  const unitWithLocalDates = { 
    ...data,
    validFrom: new Date(data.validFrom + 'Z'),
    validTo: new Date(data.validTo + 'Z'),
    lastModified: new Date(data.lastModified + 'Z') 
  };

  return (
    <>
      {updateUnitMutation.isError && (
        <ValidationSummary error={updateUnitMutation.error} />
      )}
      {updateUnitMutation.isPending ? (
        <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
          <ClipLoader size={50} color={"#123abc"} loading={true} />
          <p>{t('unit.editWait')}</p>
        </div>
      ) : (
        <>
          <UnitForm
            headerText={t('unit.edit')}
            dataToEdit={unitWithLocalDates}
            submitted={(unit) => {
              updateUnitMutation.mutate(unit, {
                onSuccess: () => {
                  toast.success(t('unit.editSuccess'));
                  history.back();
                },
                onError: () => {
                  toast.error(t('unit.editError'));
                }
              });
            }}
            deleted={canDelete ? handleUnitDelete : undefined}
          />
          <hr />
          <TerminalList />
        </>
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('unit.deleteConfirmation')}</p>
          {unitToDelete && (
            <>
              <div><strong>{t('common.name')}:</strong> {unitToDelete.name}</div>
              <div><strong>{t('unit.station')}:</strong> {unitToDelete.station?.name}</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete} disabled={deleteUnitMutation.isPending}>
            {t('buttons.delete')}
          </Button>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteUnitMutation.isPending}>
            {t('buttons.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UnitEdit;
